import React, { useCallback, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'
import { type CustomIntegration } from '../../../models/CustomIntegration'
import { useDeleteCustomIntegrationMutation } from '../../../store/customIntegration/customIntegration'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import { APPS_PAGE_PATH } from '../constants'

interface DeleteCustomIntegrationModalProps {
  show: boolean
  customIntegration: CustomIntegration
  onHide: () => void
}

export const DeleteCustomIntegrationModal: React.FC<DeleteCustomIntegrationModalProps> = ({ show, customIntegration, onHide }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const [confirmText, setConfirmText] = useState<string>('')
  const [deleteCustomIntegration, { isLoading }] = useDeleteCustomIntegrationMutation()
  const { id, name } = customIntegration

  const isConfirmed = confirmText === name

  const handleHide = useCallback(() => {
    setConfirmText('')
    onHide()
  }, [onHide])

  const handleDelete = useCallback(async () => {
    if (!isConfirmed) {
      return
    }
    try {
      await deleteCustomIntegration({ accountId, customIntegrationId: id }).unwrap()
      dispatch(addToast({ variant: 'success', title: `${name} has been deleted.` }))
      navigate(APPS_PAGE_PATH)
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to delete the MCP server. Please try again.'] }))
    }
  }, [isConfirmed, deleteCustomIntegration, accountId, id, dispatch, name, navigate])

  return (
    <Modal
      show={show}
      onHide={handleHide}
      header={`Delete ${name}?`}
      body={
        <div className='d-flex flex-column gap-3'>
          <p className='mb-0'>This action cannot be undone.</p>
          <div className='d-flex align-items-center zocks-gap-0.75 p-3 rounded-3 zocks-border-color-warning zocks-bg-color-warning zocks-text-gray-900'>
            <i className='bi bi-exclamation-triangle flex-shrink-0' />
            <span>Deleting {name} removes the MCP server for everyone in your account. Users who connected to it lose their connection.</span>
          </div>
          <Form.Group controlId='delete-custom-integration-confirm'>
            <Form.Label>Type the name to confirm</Form.Label>
            <Form.Control type='text' placeholder={name} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete='off' />
          </Form.Group>
        </div>
      }
      footer={
        <div className='d-flex gap-3'>
          <Button variant='secondary' onClick={handleHide}>
            Cancel
          </Button>
          <Button variant='danger' disabled={!isConfirmed} loading={isLoading} onClick={handleDelete}>
            <i className='bi bi-trash me-2' />
            Delete App
          </Button>
        </div>
      }
    />
  )
}
